import { formatDate, formatMoney } from '@betterment/core';
import { Box, Button, H2, H6, Separator, Sheet, Text, ToggleTip } from '@betterment/samba-react';

import { NetDeposits } from './NetDeposits';
import { TotalEarned } from './TotalEarned';

import { trackClick } from '../../../../../features/track';
import { DateRangeOption } from '../../../../../graphql/schema';
import { dateRangeDescriptionByValue } from '../../../../shared/date-range-filter/dateUtils';
import { UserBalancePerformanceQuery } from '../graphql/userPerformanceBalance.generated';

type BalanceDetailsSheetProps = {
  startDate: Date | undefined;
  endDate: Date | undefined;
  data: UserBalancePerformanceQuery['performance']['totals'] | undefined;
  dateRangeOption: DateRangeOption;
};

export const BalanceDetailsSheet: React.FC<BalanceDetailsSheetProps> = ({
  data,
  startDate,
  endDate,
  dateRangeOption,
}) => {
  if (!data || !startDate || !endDate) {
    return null;
  }

  const { startingBalance, endingBalance, userTransactions, investing, interest, fees, earned } = data;

  const handleBalanceDetailsClick = () => {
    trackClick({
      name: 'balanceDetailBreakdown',
      module: 'Performance',
      location: 'Balance',
    });
  };

  const dateRangeDescription = dateRangeDescriptionByValue(dateRangeOption, { start: startDate, end: endDate }, true);

  return (
    <Sheet overlay="weak">
      <Sheet.Trigger data-samba-component={true} asChild={true}>
        <Button variant="textPrimary" aria-label="See breakdown" onClick={handleBalanceDetailsClick}>
          See breakdown
        </Button>
      </Sheet.Trigger>
      <Sheet.Content
        css={{
          padding: 'space.400',
          maxHeight: '100vh',
          overflow: 'auto',
        }}
        size="medium"
      >
        <Sheet.Title asChild={true}>
          <Box>
            <H2 variant="title300">Balance details</H2>
            {dateRangeDescription}
          </Box>
        </Sheet.Title>
        <Separator css={{ mt: 'space.300' }} />
        <Sheet.Description asChild={true}>
          <Box css={{ display: 'grid', gap: 'space.300', mt: 'space.300' }}>
            <Box>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box css={{ display: 'grid' }}>
                  <Box css={{ display: 'flex' }}>
                    <H6 variant="title100" css={{ fontWeight: 'normal' }}>
                      Starting balance
                    </H6>
                  </Box>
                  <Text variant="text50" color="text.weak">
                    {formatDate(startDate, 'longOrdinalShortMonth')}
                  </Text>
                </Box>
                <Box>
                  <Text>{formatMoney(startingBalance)}</Text>
                </Box>
              </Box>
              <Separator css={{ mt: 'space.300' }} />
            </Box>
            <NetDeposits
              netDeposits={userTransactions.netDeposits}
              deposits={userTransactions.deposits}
              pendingDeposits={userTransactions.pending?.depositsAndTransfersIn}
              withdrawals={userTransactions.withdrawals}
              pendingWithdrawals={userTransactions.pending?.withdrawalsAndTransfersOut}
            />
            <TotalEarned
              totalEarned={earned}
              marketChanges={investing?.marketChanges}
              dividendsReinvested={investing?.dividends?.reinvested}
              dividendsPending={investing?.dividends?.pending}
              interestPaid={interest?.paid}
              managementFees={fees?.management}
            />
            {investing?.adjustments && investing.adjustments.cents !== 0 && (
              <Box>
                <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box css={{ display: 'grid' }}>
                    <Box css={{ display: 'flex' }}>
                      <H6 variant="title100" css={{ fontWeight: 'normal' }}>
                        Adjustments
                      </H6>
                      <ToggleTip>
                        <ToggleTip.Trigger iconName="InfoCircle" label="Toggle Tip" />
                        <ToggleTip.Content side="right" css={{ maxWidth: '300px', zIndex: 'compat-modal' }}>
                          Adjustments are manual debits or credits to an account. Details for adjustments are provided
                          on the activity page.
                        </ToggleTip.Content>
                      </ToggleTip>
                    </Box>
                  </Box>
                  <Box>
                    <Text>{formatMoney(investing.adjustments)}</Text>
                  </Box>
                </Box>
                <Separator css={{ mt: 'space.300' }} />
              </Box>
            )}
            <Box>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box css={{ display: 'grid' }}>
                  <Box css={{ display: 'flex' }}>
                    <H6 variant="title100" css={{ fontWeight: 'normal' }}>
                      Ending balance
                    </H6>
                  </Box>
                  <Text variant="text50" color="text.weak">
                    {formatDate(endDate, 'longOrdinalShortMonth')}
                  </Text>
                </Box>
                <Box>
                  <Text>{formatMoney(endingBalance)}</Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Sheet.Description>
      </Sheet.Content>
    </Sheet>
  );
};
